import { useRef, useState } from "react";
import { notification } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useDispatch, useStore } from "react-redux";
import { getInventories } from "../../../toolkit/slices/inventories/inventorySlice";
import {
  enrichBdfInventoryFromRedux,
  getBdfLineKey,
  hasOverlapWithSiblingBdfLines,
  withBdfLineKey,
} from "../../../../utils/bdfLineKey";
import {
  DEFAULT_SUFFIX,
  buildNewTradingLine,
  buildTradingImportTemplate,
  getTradingRowProblems,
  parseTradingExcelBuffer,
  planTradingExcelImport,
} from "../../../../utils/tradingExcelImport";

dayjs.extend(customParseFormat);

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
const MAX_FILE_BYTES = 5 * 1024 * 1024;

const INITIAL_STATE = {
  open: false,
  status: "intro",
  fileName: "",
  error: null,
  foundHeaders: null,
  info: null,
  plan: null,
  edits: {},
  showMissing: false,
  progress: null,
  creationErrors: [],
};

const authHeaders = () => ({
  "Content-Type": "application/json",
  authorization: `Token ${window.localStorage.getItem("token")}`,
});

const readFileAsArrayBuffer = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });

const fetchList = async (endpoint, key) => {
  const response = await fetch(`${apiBaseUrl}/inventoryManagement/${endpoint}`, {
    method: "GET",
    headers: authHeaders(),
  });
  const data = await response.json();
  return data?.success ? data[key] || [] : [];
};

const fetchVendors = async () => {
  const response = await fetch(`${apiBaseUrl}/inventoryManagement/getAllTradingParties`, {
    method: "GET",
    credentials: "include",
  });
  const data = await response.json();
  return data?.success ? (data.data || []).map((p) => ({ name: p?.name, gstNumber: p?.gstNumber })) : [];
};

/** Creates one trading inventory exactly like the "Create new Trading inventory" form does. */
const createTradingInventory = async ({ newInv, vendor, suffix, purchaseRate }) => {
  const body = {
    locations: { city: newInv.city, Area: newInv.area, name: newInv.name },
    Illu: newInv.illu,
    width: newInv.width,
    height: newInv.height,
    typeOfMedia: newInv.mediaVehicle,
    vendor,
    ownedBy: vendor,
    ratePerMonth: newInv.ratePerMonth,
    PurchaseRate: purchaseRate,
    isTrading: true,
    suffix,
  };
  const response = await fetch(`${apiBaseUrl}/inventoryManagement`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.inventory) {
    throw new Error(data.error || data.message || "Could not create the inventory");
  }
  return data.inventory;
};

const useTradingExcelImport = ({
  inventories,
  setInventories,
  allowDuplicateInventories,
  packageRate,
  handleChange,
  isDateBlocked,
  calcSpecialRate,
  canAddToCart,
  mapTradingInvForPicker,
}) => {
  const dispatch = useDispatch();
  const store = useStore();
  const excelInputRef = useRef(null);
  const requestRef = useRef(0);
  const importingRef = useRef(false);
  const [state, setState] = useState(INITIAL_STATE);
  const [vendors, setVendors] = useState([]);

  const update = (token, patch) => {
    if (token !== requestRef.current) return;
    setState((prev) => ({ ...prev, ...patch }));
  };

  const loadVendors = async () => {
    try {
      setVendors(await fetchVendors());
    } catch (error) {
      console.error("Could not load vendors:", error);
    }
  };

  const openTradingImport = () => {
    requestRef.current += 1;
    setState({ ...INITIAL_STATE, open: true, status: "intro" });
    loadVendors();
  };

  const closeTradingImport = () => {
    if (importingRef.current) return;
    requestRef.current += 1;
    setState((prev) => ({ ...prev, open: false }));
  };

  const openFilePicker = () => excelInputRef.current?.click();

  const handleFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file || importingRef.current) return;
    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
      notification.error({ message: "Please choose an Excel (.xlsx, .xls) or .csv file." });
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      notification.error({ message: "The file is larger than 5 MB." });
      return;
    }

    requestRef.current += 1;
    const token = requestRef.current;
    setState({ ...INITIAL_STATE, open: true, status: "loading", fileName: file.name });
    if (vendors.length === 0) loadVendors();

    try {
      const buffer = await readFileAsArrayBuffer(file);
      const parsed = parseTradingExcelBuffer(buffer);
      if (!parsed.ok) {
        update(token, { status: "error", error: parsed.error, foundHeaders: parsed.foundHeaders || null });
        return;
      }

      await dispatch(getInventories());
      const fresh = store.getState().inventoriesList.inventories || [];
      if (fresh.length === 0) throw new Error("Could not load inventories. Please try again.");
      // Own, trading and removed inventories are all checked so a location is
      // never created twice.
      const [trading, removed] = await Promise.all([
        fetchList("getAllTradingInventories", "allTradingInventories"),
        fetchList("getAllRemovedInventories", "allRemovedInventories"),
      ]);
      const seen = new Set();
      const candidates = [...trading, ...fresh, ...removed].filter((inv) => {
        if (!inv || seen.has(inv._id)) return false;
        seen.add(inv._id);
        return true;
      });

      const plan = planTradingExcelImport({
        rows: parsed.rows,
        candidates,
        existingInventories: inventories || [],
        allowDuplicateInventories,
        packageRate,
        mapTradingInv: mapTradingInvForPicker,
        createLine: (item) => withBdfLineKey(enrichBdfInventoryFromRedux({ ...item }, fresh)),
        isDateBlocked: (day, record, working) =>
          isDateBlocked(
            dayjs(day, "DD-MM-YYYY", true),
            record.Bookings,
            record.blockedDates,
            record.premiumBlock,
            record,
            { blockSiblingPeriods: allowDuplicateInventories, inventories: working },
          ),
        hasSiblingOverlap: (working, candidate, start, end) =>
          hasOverlapWithSiblingBdfLines(working, getBdfLineKey(candidate), candidate._id, start, end),
        calcSpecialRate,
        canAddToCart,
      });

      update(token, {
        status: "preview",
        info: {
          sheetName: parsed.sheetName,
          matched: parsed.matched,
          ignored: parsed.ignored,
          warnings: parsed.warnings,
        },
        plan,
      });
    } catch (error) {
      console.error("Trading Excel import failed:", error);
      update(token, { status: "error", error: error?.message || "Could not process the file." });
    }
  };

  const setEdit = (groupKey, patch) =>
    setState((prev) => ({ ...prev, edits: { ...prev.edits, [groupKey]: { ...(prev.edits[groupKey] || {}), ...patch } } }));

  /** Applies a vendor / suffix to every new inventory in the preview. */
  const setEditForAll = (patch) =>
    setState((prev) => {
      const edits = { ...prev.edits };
      (prev.plan?.rows || []).forEach((row) => {
        if (row.status === "ready" && row.kind === "new") {
          edits[row.groupKey] = { ...(edits[row.groupKey] || {}), ...patch };
        }
      });
      return { ...prev, edits };
    });

  const confirmTradingImport = async () => {
    const { plan, edits } = state;
    if (!plan || plan.readyCount === 0 || importingRef.current) return;

    if (getTradingRowProblems(plan, edits).length > 0) {
      setState((prev) => ({ ...prev, showMissing: true }));
      notification.error({ message: "Select a vendor and a buying rate for every new inventory first." });
      return;
    }

    importingRef.current = true;
    const token = requestRef.current;
    const groups = [];
    const seen = new Set();
    plan.rows.forEach((row) => {
      if (row.status === "ready" && row.kind === "new" && !seen.has(row.groupKey)) {
        seen.add(row.groupKey);
        groups.push(row);
      }
    });

    const created = new Map();
    const failed = new Map();
    setState((prev) => ({ ...prev, status: "importing", progress: { done: 0, total: groups.length } }));

    try {
      for (let i = 0; i < groups.length; i += 1) {
        const row = groups[i];
        const edit = edits[row.groupKey] || {};
        try {
          const inventory = await createTradingInventory({
            newInv: row.newInv,
            vendor: edit.vendor ?? row.vendor,
            suffix: edit.suffix || row.suffix || DEFAULT_SUFFIX,
            purchaseRate: Number(edit.purchaseRate ?? row.newInv.purchaseRate),
          });
          created.set(row.groupKey, inventory);
        } catch (error) {
          failed.set(row.groupKey, error?.message || "Could not create the inventory");
        }
        if (token === requestRef.current) {
          setState((prev) => ({ ...prev, progress: { done: i + 1, total: groups.length } }));
        }
      }

      const fresh = store.getState().inventoriesList.inventories || [];
      const lines = [];
      let skipped = 0;
      plan.rows.forEach((row) => {
        if (row.status !== "ready") return;
        if (row.kind === "existing") {
          lines.push(row.line);
        } else if (created.has(row.groupKey)) {
          const base = mapTradingInvForPicker(created.get(row.groupKey));
          lines.push(
            buildNewTradingLine({
              base,
              row,
              packageRate,
              calcSpecialRate,
              withKey: (line) => withBdfLineKey(enrichBdfInventoryFromRedux(line, fresh)),
            }),
          );
        } else {
          skipped += 1;
        }
      });

      if (lines.length > 0) {
        const finalList = [...(inventories || []), ...lines];
        setInventories((prev) => [...prev, ...lines]);
        if (handleChange && finalList.every((item) => item.endDate)) {
          let max = 0;
          finalList.forEach((item) => {
            const start = dayjs(item.startDate, "DD-MM-YYYY");
            const end = dayjs(item.endDate, "DD-MM-YYYY");
            if (start.isValid() && end.isValid()) max = Math.max(max, end.diff(start, "day"));
          });
          handleChange("campaignDuration", max + 1);
        }
      }

      const problems = plan.errorCount + skipped;
      notification[failed.size > 0 ? "warning" : "success"]({
        message: `${lines.length} inventor${lines.length === 1 ? "y" : "ies"} imported${
          created.size > 0 ? ` (${created.size} new trading inventor${created.size === 1 ? "y" : "ies"} created)` : ""
        }`,
        description:
          failed.size > 0
            ? `Could not create ${failed.size}: ${[...failed.values()].slice(0, 3).join("; ")}`
            : problems > 0
              ? `${problems} row${problems === 1 ? " was" : "s were"} skipped.`
              : undefined,
        duration: failed.size > 0 ? 10 : 4.5,
      });
      importingRef.current = false;
      if (token === requestRef.current) setState((prev) => ({ ...prev, open: false, progress: null }));
    } catch (error) {
      console.error("Trading Excel import failed:", error);
      importingRef.current = false;
      notification.error({ message: error?.message || "The import failed." });
      if (token === requestRef.current) setState((prev) => ({ ...prev, status: "preview", progress: null }));
    }
  };

  const downloadTemplate = () => {
    const bytes = buildTradingImportTemplate();
    const blob = new Blob([bytes], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "Trading_Inventory_Import_Template.xlsx";
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return {
    excelInputRef,
    tradingImport: state,
    vendors,
    openTradingImport,
    closeTradingImport,
    openFilePicker,
    handleFileChange,
    setEdit,
    setEditForAll,
    confirmTradingImport,
    downloadTemplate,
  };
};

export default useTradingExcelImport;
